// lifetime-thickness-sections-v0 reader (docs/contracts/lifetime-thickness-sections-v0.md, ba-life's draft at 21168c51): LIFE-020's
// assigned-thickness sections, the app-read sibling of lifetime-sections-v0 for Lifetime's Assigned thickness track. Four fine-mesh
// tracks, one an assigned layer thickness (h = 1.5, 2, 3 and 4 mm), each the closed 2D section of ba:left-cerebral-white-matter in RAS
// mm at the growth states its case accepted: a prefix of the requested imposed-growth axis (g = 1 … 2.6875 in 1/16 steps), which is
// the model's own axis and never an age, seconds or a clock. Within a track's accepted range the two bracketing states are blended
// linearly for display (the same material nodes; a derived picture, not another equilibrium); outside it there is no data, and the
// last accepted state is a separate, labelled choice. Never between thicknesses. Never sent to the viewport: the app draws the
// sections in the strip, and the engine goes on morphing the atlas beside them.

import type { SimFiles } from './files.ts';
import type { ThicknessSectionsHeader, ThicknessTrackHeader } from './types.ts';

/** The requested axis's step, 1/16 of growth. */
export const GROWTH_STEP = 0.0625;

export interface ThicknessTrack {
  id: string;
  /** The producer's label, "h = 1.5 mm". */
  label: string;
  /** The assigned layer thickness, mm: what the picker keys on. */
  thicknessMm: number;
  mesh: string | null;
  /** The accepted growth states, in order: a prefix of the requested axis, empty for a case that never started. */
  growth: Float64Array;
  frames: number;
  points: number;
  /** `frames × points` xyz triples, frame-major; null for a track with no accepted state (no data, never a zero-valued surface). */
  positions: Float32Array | null;
  /** The section's sagittal plane, RAS x in mm; null where the track states none. */
  planeX: number | null;
  /** The case's status in the producer's words (completed, stopped, not_started…). */
  caseStatus: string;
  /** The last accepted growth, from the axis; null for a track that never started. */
  lastAccepted: number | null;
  /** Loads attempted, accepted or not. */
  attemptedLoads: number;
  /** The first rejected load's growth, where the gate rejected one. */
  failedAt: number | null;
}

/** One track's outline at a growth: the loop (xyz triples, the last point joining the first), the frames blended and the later one's
 *  weight; `exact` for a saved state as stored. */
export interface Section {
  loop: Float32Array;
  growth: number;
  i: number;
  j: number;
  w: number;
  exact: boolean;
}

export interface ThicknessMetrics {
  /** The registered result, copied: its `result` verdict and whatever else the producer recorded. */
  result: Record<string, unknown> | null;
  /** The whole-set numerical gate: `passed`, its gates by name, and its per-frame rows. */
  gate: { passed: boolean | null; gates: Record<string, boolean>; rows: Record<string, unknown>[] } | null;
  units: Record<string, string>;
  note: string | null;
  /** The producer's own flag: metrics.json computes no new score. */
  newScoring: boolean | null;
}

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** The frames around `g` within an accepted range and the later one's weight; null outside it. No clamping: the contract returns no
 *  data past a track's range, and a caller shows the last accepted state only as its own labelled choice (`lastAccepted`). */
export function bracketGrowth(growth: ArrayLike<number>, g: number): { i: number; j: number; w: number } | null {
  const n = growth.length;
  if (!n || !Number.isFinite(g) || g < growth[0]! || g > growth[n - 1]!) return null;
  let i = 0;
  while (i < n - 1 && growth[i + 1]! <= g) i++;
  if (i === n - 1 || growth[i] === g) return { i, j: i, w: 0 };
  return { i, j: i + 1, w: (g - growth[i]!) / (growth[i + 1]! - growth[i]!) };
}

/** A closed loop's unsigned polygon area in the sagittal plane (RAS y, z), mm²: a display-derived description of the outline drawn,
 *  not one of the run's registered measures. */
export function loopArea(loop: Float32Array): number {
  const n = Math.floor(loop.length / 3);
  let s = 0;
  for (let k = 0; k < n; k++) {
    const m = (k + 1) % n;
    s += loop[k * 3 + 1]! * loop[m * 3 + 2]! - loop[m * 3 + 1]! * loop[k * 3 + 2]!;
  }
  return Math.abs(s) / 2;
}

type Parsed = Omit<ThicknessTrack, 'positions'> & { descriptor: ThicknessTrackHeader['positions'] };

function parseTrack(t: ThicknessTrackHeader, k: number, where: string, requested: number[] | null): Parsed {
  const id = typeof t.id === 'string' && t.id ? t.id : null;
  if (!id) throw new Error(`${where}: tracks[${k}] has no id`);
  const who = `${where}: track ${id}`;
  const h = t.thickness_mm;
  if (!num(h) || !(h > 0)) throw new Error(`${who} has no thickness_mm`);
  const values = t.axis?.values;
  if (!Array.isArray(values) || !values.every(num)) throw new Error(`${who} has no axis.values`);
  for (let i = 1; i < values.length; i++) if (!(values[i]! > values[i - 1]!)) throw new Error(`${who}: axis.values must be strictly increasing`);
  // Accepted prefixes only: the states are the requested axis's first `frames` values, never a later one with an earlier one missing.
  if (requested && values.some((v, i) => requested[i] !== v)) throw new Error(`${who}: axis.values is not a prefix of the requested axis`);
  const frames = values.length;
  if (num(t.frames) && t.frames !== frames) throw new Error(`${who}: frames says ${t.frames}, axis.values has ${frames}`);
  const d = t.positions ?? null;
  let points = num(t.points) ? t.points : 0;
  if (frames) {
    if (!d) throw new Error(`${who}: ${frames} accepted states without positions`);
    const shape = d.shape;
    if (!Array.isArray(shape) || shape.length !== 3 || shape[0] !== frames || shape[2] !== 3) throw new Error(`${who}: positions.shape is ${JSON.stringify(shape)}, not [${frames}, points, 3]`);
    if (points && shape[1] !== points) throw new Error(`${who}: points says ${points}, positions.shape ${shape[1]}`);
    points = shape[1]!;
    if (!(points > 2)) throw new Error(`${who}: a section needs at least three points`);
  } else if (d) throw new Error(`${who}: no accepted state, yet positions (no data must stay no data)`);
  const px = t.plane?.x_mm;
  const failed = t.failed_attempt;
  return {
    id,
    label: typeof t.label === 'string' && t.label ? t.label : `h = ${h} mm`,
    thicknessMm: h,
    mesh: typeof t.mesh === 'string' ? t.mesh : null,
    growth: Float64Array.from(values),
    frames,
    points,
    planeX: num(px) ? px : null,
    caseStatus: typeof t.case_status === 'string' && t.case_status ? t.case_status : frames ? 'unknown' : 'not_started',
    lastAccepted: frames ? values[frames - 1]! : null,
    attemptedLoads: num(t.attempted_loads) && t.attempted_loads >= frames ? t.attempted_loads : frames,
    failedAt: failed && typeof failed === 'object' && num(failed.growth) ? failed.growth : null,
    descriptor: d,
  };
}

export class ThicknessSectionsSim {
  private metricsP: Promise<ThicknessMetrics | null> | null = null;

  private constructor(
    readonly files: SimFiles,
    readonly header: ThicknessSectionsHeader,
    /** The tracks in the header's order, one an assigned thickness. */
    readonly tracks: ThicknessTrack[],
    /** The requested axis, every growth state a case was asked for; null where the header lists none. */
    readonly requested: number[] | null,
    /** The sections' extent in the sagittal plane over every track and frame, RAS y and z, mm: [[y0, z0], [y1, z1]]; null with no
     *  geometry at all. The header's fixed bounds where it gives them (one physical scale for all four tracks), else measured. */
    readonly boundsYZ: [[number, number], [number, number]] | null,
  ) {}

  static async open(files: SimFiles, raw: ThicknessSectionsHeader): Promise<ThicknessSectionsSim> {
    const where = `sims/${raw.id}`;
    const list = raw.tracks;
    if (!Array.isArray(list) || !list.length) throw new Error(`${where}: a lifetime-thickness-sections-v0 run without tracks`);
    const req = raw.axis?.requested_values;
    const requested = Array.isArray(req) && req.length > 0 && req.every(num) ? [...req] : null;
    if (requested) for (let i = 1; i < requested.length; i++) if (!(requested[i]! > requested[i - 1]!)) throw new Error(`${where}: axis.requested_values must be strictly increasing`);
    const parsed = list.map((t, k) => parseTrack(t, k, where, requested));
    const seen = new Set<number>();
    for (const p of parsed) {
      if (seen.has(p.thicknessMm)) throw new Error(`${where}: two tracks at h = ${p.thicknessMm} mm`);
      seen.add(p.thicknessMm);
    }
    const arrays = await Promise.all(parsed.map((p) => (p.descriptor ? files.array(p.descriptor) : Promise.resolve(null))));
    const tracks: ThicknessTrack[] = parsed.map(({ descriptor: _d, ...p }, k) => {
      const a = arrays[k] ?? null;
      if (a !== null && !(a instanceof Float32Array)) throw new Error(`${where}: track ${p.id}: positions must be <f4`);
      if (a && a.length !== p.frames * p.points * 3) throw new Error(`${where}: track ${p.id}: positions has ${a.length} values, not ${p.frames} × ${p.points} × 3`);
      return { ...p, positions: a };
    });
    const b = raw.bounds_mm;
    const given = Array.isArray(b) && b.length === 2 && b.every((r) => Array.isArray(r) && r.length === 3 && r.every(num)) ? b : null;
    let bounds: [[number, number], [number, number]] | null = given ? [[given[0][1], given[0][2]], [given[1][1], given[1][2]]] : null;
    if (!bounds) {
      let y0 = Infinity;
      let z0 = Infinity;
      let y1 = -Infinity;
      let z1 = -Infinity;
      for (const t of tracks) {
        const arr = t.positions;
        if (!arr) continue;
        for (let k = 0; k < arr.length; k += 3) {
          const y = arr[k + 1]!;
          const z = arr[k + 2]!;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
          if (z < z0) z0 = z;
          if (z > z1) z1 = z;
        }
      }
      bounds = y0 <= y1 ? [[y0, z0], [y1, z1]] : null;
    }
    return new ThicknessSectionsSim(files, raw, tracks, requested, bounds);
  }

  /** The whole-set verdict the display copies (survives, killed, NOT SCORED…), in the producer's words. */
  get verdict(): string {
    const v = this.header.source_result?.verdict;
    return typeof v === 'string' && v ? v : 'unknown';
  }

  /** The axis's span: the requested states' where listed, else over the tracks' accepted states. */
  get range(): [number, number] {
    if (this.requested) return [this.requested[0]!, this.requested[this.requested.length - 1]!];
    let lo = Infinity;
    let hi = -Infinity;
    for (const t of this.tracks)
      if (t.frames) {
        lo = Math.min(lo, t.growth[0]!);
        hi = Math.max(hi, t.growth[t.frames - 1]!);
      }
    return lo <= hi ? [lo, hi] : [1, 1];
  }

  /** The thicknesses on offer, mm, ascending. */
  get thicknesses(): number[] {
    return this.tracks.map((t) => t.thicknessMm).sort((a, b) => a - b);
  }

  /** The track at an assigned thickness, exactly (no interpolation between thicknesses); null for one the run didn't solve. */
  trackAt(thicknessMm: number): ThicknessTrack | null {
    return this.tracks.find((t) => Math.abs(t.thicknessMm - thicknessMm) < 1e-9) ?? null;
  }

  /** A track's section at a growth within its accepted range: a saved state as stored, or the two around it blended node by node
   *  (display only); null outside the range, and for a track that never started. */
  sectionAt(track: ThicknessTrack, g: number): Section | null {
    const b = bracketGrowth(track.growth, g);
    if (!b || !track.positions) return null;
    return { loop: this.frame(track, b.i, b.j, b.w), growth: g, ...b, exact: b.w === 0 };
  }

  /** A track's last accepted state, as stored, for the explicitly labelled endpoint view; null for a track that never started. */
  lastAccepted(track: ThicknessTrack): Section | null {
    if (!track.positions || !track.frames) return null;
    const i = track.frames - 1;
    return { loop: this.frame(track, i, i, 0), growth: track.growth[i]!, i, j: i, w: 0, exact: true };
  }

  private frame(track: ThicknessTrack, i: number, j: number, w: number): Float32Array {
    const n = track.points * 3;
    const out = new Float32Array(n);
    const a = track.positions!.subarray(i * n, (i + 1) * n);
    if (i === j || w === 0) out.set(a);
    else {
      const b = track.positions!.subarray(j * n, (j + 1) * n);
      for (let k = 0; k < n; k++) out[k] = a[k]! + w * (b[k]! - a[k]!);
    }
    return out;
  }

  /** metrics.json: the registered result and the whole-set numerical gate, copied by the producer; no new score. */
  metrics(): Promise<ThicknessMetrics | null> {
    return (this.metricsP ??= this.files.maybeJson<Record<string, unknown>>(this.header.metrics ?? 'metrics.json').then((m) => {
      if (!m || typeof m !== 'object') return null;
      const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null);
      const g = obj(m.numerical_gate);
      const gates = obj(g?.gates);
      const gate = g
        ? {
            passed: typeof g.passed === 'boolean' ? g.passed : null,
            gates: gates ? (Object.fromEntries(Object.entries(gates).filter(([, v]) => typeof v === 'boolean')) as Record<string, boolean>) : {},
            rows: Array.isArray(g.rows) ? g.rows.filter((r): r is Record<string, unknown> => !!obj(r)) : [],
          }
        : null;
      const units = obj(m.units);
      return {
        result: obj(m.result),
        gate,
        units: units ? (Object.fromEntries(Object.entries(units).filter(([, v]) => typeof v === 'string')) as Record<string, string>) : {},
        note: typeof m.note === 'string' ? m.note : null,
        newScoring: typeof m.new_scoring === 'boolean' ? m.new_scoring : null,
      };
    }));
  }
}
